import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { AsyncStatus } from '../../hooks/useAsync';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';

interface ModalProps {
  isOpen: boolean;
  instanceId: string;
  instanceName: string;
  onRequestClose: () => void;
}

export const ReactivateCEModal: React.FC<ModalProps> = ({ isOpen, instanceId, instanceName, onRequestClose }) => {
  const { t } = useTranslation();
  const { reactivateCE } = useTaniumInstances();
  const { showNotification } = useToastNotificationsControl();

  useEffect(() => {
    if (reactivateCE.status === AsyncStatus.Success) {
      onRequestClose();
      reactivateCE.reset();
      showNotification({
        type: 'success',
        contents: t('Successfully initiated reactivation of instanceName', { instanceName }),
      });
    } else if (reactivateCE.status === AsyncStatus.Failure) {
      onRequestClose();
      reactivateCE.reset();
      showNotification({
        type: 'error',
        contents: t('Failed to reactivate instanceName', { instanceName }),
      });
    }
  }, [onRequestClose, reactivateCE, showNotification, t, instanceName]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      confirmLabel={t('Reactivate')}
      cancelLabel={t('Close')}
      header={t('Reactivate Tanium Instance')}
      onConfirm={async () => {
        await reactivateCE.execute(instanceId);
      }}
      isConfirmDisabled={reactivateCE.status === AsyncStatus.Pending}
      onCancel={() => {
        onRequestClose();
        reactivateCE.reset();
      }}
    >
      <>{t('Are you sure you want to reactivate instanceName?', { instanceName })}</>
    </ConfirmModal>
  );
};
